import { ActivityIcon, ActivityTimeline, Button, InlineMessage, Stack, Text, type TimelineEntry } from '@olytic/foundation';

import { formatDateTime } from '../objects/format';
import { entryKey, hasLongToken, HISTORY_ERROR, type HistoryEntry, historyWords, nextStep, type RecordHistory } from './historyApi';

/** A value as plain text; one with a word too long to wrap at a space breaks anywhere. */
function Value({ text }: { text: string }): JSX.Element {
  return hasLongToken(text) ? <span className="break-all">{text}</span> : <>{text}</>;
}

/** One change as a timeline entry: "<Name> changed <Field Label> from <old> to <new>", and when. */
export function timelineEntryOf(entry: HistoryEntry): TimelineEntry {
  const w = historyWords(entry);
  return {
    id: entryKey(entry),
    title:
      w.from === undefined ? (
        <>{w.actor} set {w.label} to <Value text={w.to} /></>
      ) : (
        <>{w.actor} changed {w.label} from <Value text={w.from} /> to <Value text={w.to} /></>
      ),
    timestamp: formatDateTime(entry.changedAt),
    icon: <ActivityIcon />,
  };
}

/**
 * The record page's History section (MOTHER-83), for the state
 * useRecordHistory answers: who changed each tracked field and when, newest
 * first, 10 at a time. Nothing while
 * loading, for a 404 or with no entries; the heading and a one-line message
 * when the first page failed; else the shown entries and "Show more" while
 * there are more.
 */
export function RecordHistorySection({ history, onShowMore }: { history: RecordHistory; onShowMore: () => void }): JSX.Element | null {
  if (history.state === 'loading' || history.state === 'hidden') return null;
  return (
    <section aria-label="History">
      <Stack gap="sm">
        <Text as="h3" size="md">History</Text>
        {history.state === 'error' ? (
          <InlineMessage tone="error" role="alert">{HISTORY_ERROR}</InlineMessage>
        ) : (
          <>
            <ActivityTimeline entries={history.entries.slice(0, history.shown).map(timelineEntryOf)} />
            {history.moreFailed ? <InlineMessage tone="error" role="alert">{HISTORY_ERROR}</InlineMessage> : null}
            {nextStep(history.entries.length, history.shown, history.hasMore) === 'none' ? null : (
              <div>
                <Button variant="neutral" size="sm" loading={history.moreBusy} onClick={onShowMore}>
                  Show more
                </Button>
              </div>
            )}
          </>
        )}
      </Stack>
    </section>
  );
}
